import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { useFlw1235EnterpriseNavCtaOverride } from '@educator/experiments/flw-1235_v2_enterprise_nav_cta'
import {
  IconHamburgerMenuDefault,
  Modal,
  ModalClose,
  ModalContent,
} from '@mc/design-system'
import {
  AccordionMenu,
  CertificatesSection,
  getProducts,
  useIsNewForBusinessPageEnabled,
} from '@mc/global-navigation'
import { useTranslation } from '@mc/i18n'
import { useMobileMenuPanel } from '../../hooks/useMobileMenuPanel'
import { SubjectsSection } from './SubjectsSection'
import classes from './TopNavigation.module.scss'

interface MobileMenuProps {
  burgerWhite?: boolean
}

/**
 * Button + modal that shows the global navigation accordion menu.
 * Reusable in different layout contexts (TopNavigation bar, NavBar, etc.)
 */
export const MobileMenuButton = () => {
  const { t } = useTranslation('@mc/educator')
  const router = useRouter()
  const isSubjectPage = router.pathname.startsWith('/subjects')
  const [show, setShow] = useState(false)
  const { activePanel, openPanel, closePanel } = useMobileMenuPanel()
  const newForBusinessPageEnabled = useIsNewForBusinessPageEnabled()
  const enterpriseCtaCopy = useFlw1235EnterpriseNavCtaOverride()

  const products = getProducts({
    pathname: '/',
    query: {},
    newForBusinessPageEnabled,
    productType: 'certificates',
    onCallLinksEnabled: true,
    onShowCertificatesSection: () => openPanel('certificates'),
    enterpriseCtaCopy,
  })

  const productsWithSubjects = products.map((product) =>
    product.type === 'executive'
      ? {
          ...product,
          links: [
            ...product.links,
            {
              title: t('subjectLandingPage.subjectNav.subjectsFallback'),
              onClick: () => openPanel('subjects'),
            },
          ],
        }
      : product,
  )

  let menuContent
  if (activePanel === 'certificates') {
    menuContent = <CertificatesSection onHideCertificatesSection={closePanel} />
  } else if (activePanel === 'subjects') {
    menuContent = <SubjectsSection onHideSubjectsSection={closePanel} />
  } else {
    menuContent = (
      <AccordionMenu
        key={isSubjectPage ? 'subject' : 'default'}
        products={productsWithSubjects}
        defaultExpandedProduct={isSubjectPage ? 'executive' : 'certificates'}
        productSectionClassName='mc-bg-container-tint-medium'
      />
    )
  }

  return (
    <div className='mc-theme-dark'>
      <button
        className='mc-theme-light'
        onClick={() => setShow(true)}
        aria-label='Open navigation menu'
      >
        <IconHamburgerMenuDefault size='sm' />
      </button>
      <Modal
        appendToBody
        show={show}
        onClose={() => {
          setShow(false)
          closePanel()
        }}
        className='mc-theme-dark mc-bg-container-tint-dark'
      >
        <ModalContent className='mc-modal__content--fullscreen c-px-5'>
          <div
            className='d-flex flex-row-reverse'
            style={{
              borderBottom: '1px solid var(--mc-theme-dropdown-border)',
            }}
          >
            <ModalClose className='position-relative' />
          </div>
          {menuContent}
        </ModalContent>
      </Modal>
    </div>
  )
}

/**
 * MobileMenu with layout wrapper for the TopNavigation bar.
 */
export const MobileMenu = ({ burgerWhite = false }: MobileMenuProps) => (
  <div
    className={`d-lg-none mc-py-1 d-flex justify-content-end mc-px-4 ${burgerWhite ? classes.burgerWhite : ''}`}
  >
    <MobileMenuButton />
  </div>
)
